const COMPRESSIBLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);

const MAX_IMAGE_DIMENSION = 1920;
const JPEG_WEBP_QUALITY = 0.82;
const MIN_SIZE_TO_COMPRESS_BYTES = 512 * 1024;

function canCompressInBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to decode image for compression.'));
    };

    image.src = objectUrl;
  });
}

function toCompressedBlob(canvas: HTMLCanvasElement, mimeType: string): Promise<Blob | null> {
  // PNG keeps browser-default (lossless) encoding; compression gain mainly comes from resizing dimensions.
  const quality = mimeType === 'image/png' ? undefined : JPEG_WEBP_QUALITY;

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

export async function compressImageForUpload(file: File): Promise<File> {
  const normalizedType = String(file.type || '').toLowerCase();

  if (!canCompressInBrowser()) return file;
  if (!COMPRESSIBLE_IMAGE_TYPES.has(normalizedType)) return file;
  if (file.size < MIN_SIZE_TO_COMPRESS_BYTES) return file;

  try {
    const image = await loadImageElement(file);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;

    if (!sourceWidth || !sourceHeight) return file;

    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(sourceWidth, sourceHeight));
    const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
    const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext('2d');
    if (!context) return file;

    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const compressedBlob = await toCompressedBlob(canvas, normalizedType || 'image/jpeg');
    if (!compressedBlob || compressedBlob.size <= 0) return file;
    if (compressedBlob.size >= file.size) return file;

    return new File([compressedBlob], file.name, {
      type: compressedBlob.type || file.type,
      lastModified: file.lastModified
    });
  } catch {
    return file;
  }
}
